import axios from "axios";

// Base URL for API
const API_BASE_URL = "https://pukfh1sc9a.execute-api.ap-south-1.amazonaws.com/prod";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Error handler for all API requests
const handleApiError = (error) => {
  console.error("API Error:", error);
  if (error.response) {
    console.error("Server Error Response:", error.response.data);
    console.error("Status:", error.response.status);
    if (error.response.status === 500) {
      throw new Error("Server error. Please try again later.");
    }
  } else if (error.request) {
    console.error("No response received from server");
    throw new Error("No response from server. Please check your connection.");
  } else {
    console.error("Error setting up request:", error.message);
  }
  throw error;
};

// Fetch comprehensive leads data with filters
export const fetchLeadsData = async (filters = {}, page = 1, limit = 20, lastEvaluatedKey = null) => {
  try {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'all') {
        queryParams.append(key, value);
      }
    });
    queryParams.append("limit", limit);
    if (lastEvaluatedKey) queryParams.append("lastEvaluatedKey", lastEvaluatedKey);
    
    const response = await apiClient.get(`/api/leads/getComprehensiveAnalytics?${queryParams.toString()}`);
    return {
      leads: response.data.leads || [],
      count: response.data.count || 0,
      lastEvaluatedKey: response.data.lastEvaluatedKey,
      hasMore: response.data.hasMore
    };
  } catch (error) {
    return handleApiError(error);
  }
};

// Export leads data to CSV
export const exportLeadsToCSV = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') queryParams.append(key, value);
    });
    queryParams.append("export", "true");
    
    const response = await apiClient.get(`/api/leads/getComprehensiveAnalytics?${queryParams.toString()}`);
    return response.data.csvData;
  } catch (error) {
    return handleApiError(error);
  }
};

// Fetch today's leads count
export const fetchTodayLeadsCount = async () => {
  try {
    const response = await apiClient.get("/api/leads/getTodayLeadsCount");
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Fetch agents with proper error handling and fallback
export const fetchAgents = async () => {
  try {
    const response = await apiClient.get("/api/leads/getAgentName");
    console.log(response.data)
    
    // Check if the response data has the expected structure
    if (response.data && Array.isArray(response.data)) {
      return response.data; // Return the array directly since backend now returns formatted data
    } else {
      console.warn("Unexpected response format from agents endpoint:", response.data);
      return [];
    }
  } catch (error) {
    console.warn("Error fetching agents, using defaults:", error);
    return [
      { id: "1", name: "Priya Agarwal" },
      { id: "2", name: "Rahul Sharma" },
      { id: "3", name: "Amit Verma" },
      { id: "4", name: "Sneha Singh" },
      { id: "5", name: "Nikhil Jain" },
    ];
  }
};

// Fetch lead sources with proper error handling
export const fetchLeadSources = async () => {
  try {
    const response = await apiClient.get("/api/leads/getLeadSource");
    console.log(response.data)
    
    // Check if the response has the expected data structure
    if (response.data && Array.isArray(response.data)) {
      return response.data; // Return the array directly
    } else {
      console.warn("Unexpected response format from lead sources endpoint:", response.data);
      return [];
    }
  } catch (error) {
    console.error('Error fetching lead sources:', error);
    return [];
  }
};

// Fetch lead statuses with proper error handling
export const fetchLeadStatuses = async () => {
  try {
    const response = await apiClient.get("/api/leads/getLeadStatuses");
    console.log(response.data)
    // Check if the response has the expected data structure
    if (response.data && Array.isArray(response.data)) {
      return response.data; // Return the array directly
    } else {
      console.warn("Unexpected response format from lead statuses endpoint:", response.data);
      return [];
    }
  } catch (error) {
    console.error('Error fetching lead statuses:', error);
    return [];
  }
};

// Fetch comprehensive analytics with search and filtering - FIXED
export const fetchComprehensiveAnalytics = async ({
  searchTerm = '',
  startDate = '',
  endDate = '',
  agentName = 'all',
  leadSource = 'all',
  leadStatus = 'all',
  lastEvaluatedKey = null,
  limit = 20,
  export: isExport = false
}) => {
  try {
    // Use apiClient directly instead of fetch for consistency
    const queryParams = new URLSearchParams();
    
    if (searchTerm) queryParams.append('searchTerm', searchTerm);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    if (agentName && agentName !== 'all') queryParams.append('agentName', agentName);
    if (leadSource && leadSource !== 'all') queryParams.append('leadSource', leadSource);
    if (leadStatus && leadStatus !== 'all') queryParams.append('leadStatus', leadStatus);
    if (lastEvaluatedKey) queryParams.append('lastEvaluatedKey', lastEvaluatedKey);
    queryParams.append('limit', limit.toString());
    if (isExport) queryParams.append('export', 'true');
    
    const response = await apiClient.get(`/api/leads/getComprehensiveAnalytics?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching comprehensive analytics:', error);
    return { leads: [], count: 0, lastEvaluatedKey: null, hasMore: false };
  }
};

// Fetch agent-wise analytics with period aggregation
export const fetchAgentwiseAnalytics = async (agentName, startDate, endDate, period = 'daily') => {
  if (!agentName || agentName === 'all') {
    return [];
  }
  
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('agentName', agentName);
    
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    if (period) queryParams.append('period', period);
    
    const response = await apiClient.get(`/api/leads/getAgentwiseAnalytics?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching agent-wise analytics:', error);
    return [];
  }
};

// Fetch lead source analytics
export const fetchLeadSourceAnalytics = async (leadSource, startDate, endDate) => {
  if (!leadSource || leadSource === 'all') {
    return [];
  }
  
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('leadSource', leadSource);
    
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    
    const response = await apiClient.get(`/api/leads/getLeadSourceWiseAnalytics?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching lead source analytics:', error);
    return [];
  }
};

// Add a lead
export const addLead = async (leadData) => {
  try {
    const response = await apiClient.post("/api/leads/webhook", leadData);
    return response.data;
  } catch (error) {
    return handleApiError(error);
  }
};

// Fetch agent names for dropdown (using apiClient for consistency)
export const fetchAgentNames = async () => {
  try {
    const response = await apiClient.get("/api/leads/getAgentName");
    return response.data;
  } catch (error) {
    console.error('Error fetching agent names:', error);
    return [];
  }
};

export default apiClient;